import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { GlassCard } from '@/components/GlassCard';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Seo } from '@/components/Seo';

const MAX_POLLS = 10;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | expired | timeout | error
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === 'paid') { setState('paid'); return; }
        if (data.payment_status === 'expired') { setState('expired'); return; }
      } catch {
        if (cancelled) return;
        setState('error');
        return;
      }
      if (polls.current >= MAX_POLLS) { setState('timeout'); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="relative min-h-screen th-hero-radial">
      <Seo title="Payment | Thirrsty.com" path="/payment/success" noindex />
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-10">
        <div className="mb-6"><Logo /></div>
        <GlassCard className="p-6 text-center th-float-in" data-testid="payment-success-card">
          {state === 'checking' && (
            <div className="py-6" data-testid="payment-checking-block">
              <Loader2 className="mx-auto h-10 w-10 text-[#F6B24A] th-spin" />
              <h1 className="mt-4 font-display text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Confirming your payment…</h1>
              <p className="mt-2 text-sm text-white/70">Hang tight — this usually takes a few seconds.</p>
            </div>
          )}
          {state === 'paid' && (
            <div className="py-6" data-testid="payment-paid-block">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-500/15 text-emerald-300">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h1 className="mt-4 font-display text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Payment successful!</h1>
              <p className="mt-2 text-sm text-white/70">Your subscription is active. Your venue is live on Thirrsty.</p>
              <Button asChild className="mt-6 h-11 w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="payment-success-view-subscription">
                <Link to="/profile/subscription">View my subscription</Link>
              </Button>
            </div>
          )}
          {(state === 'expired' || state === 'timeout' || state === 'error') && (
            <div className="py-6" data-testid="payment-problem-block">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-amber-500/15 text-amber-300">
                <AlertCircle className="h-8 w-8" />
              </div>
              <h1 className="mt-4 font-display text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
                {state === 'expired' ? 'Payment session expired' : "We couldn't confirm yet"}
              </h1>
              <p className="mt-2 text-sm text-white/70">
                {state === 'expired'
                  ? 'The checkout session expired before payment completed. No charge was made.'
                  : "If you completed payment, it may still be processing. Check your subscription in a minute — or contact us if you were charged."}
              </p>
              <Button asChild className="mt-6 h-11 w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="payment-problem-back">
                <Link to="/profile/subscription">Back to my subscription</Link>
              </Button>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
